import { useEffect, useState } from "react";
import type { Bootstrap, Bill } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, daysBetween } from "../lib/format";

// A bill is a genuine one-off only if its start/end sit within a single cycle
// (~4-5 weeks). A bounded *recurring* bill (e.g. a loan ending in 2027) also has
// both dates but spans many months, so it stays "Monthly".
const ONE_CYCLE_DAYS = 45;
function isOneOff(b: { startDate?: string | null; endDate?: string | null }): boolean {
  return Boolean(b.startDate && b.endDate) && daysBetween(b.startDate!, b.endDate!) <= ONE_CYCLE_DAYS;
}
import BillModal from "../components/BillModal";
import Select from "../components/Select";
import Segmented from "../components/Segmented";
import { useConfirm } from "../components/ConfirmProvider";
import { EditIcon, ArchiveIcon, RestoreIcon } from "../components/icons";

// Set up / manage every recurring payment. Same form as This Month's add, with
// filters and a proper table. Removing archives (keeps history) rather than deletes.
export default function Recurring({ data, reload }: { data: Bootstrap | null; reload: () => Promise<void> }) {
  const [bills, setBills] = useState<Bill[] | null>(null);
  const [modal, setModal] = useState(false);
  const [editBill, setEditBill] = useState<Bill | null>(null);
  const [search, setSearch] = useState("");
  const [person, setPerson] = useState("both");
  const [typeFilter, setTypeFilter] = useState("all");
  const [sort, setSort] = useState<"name" | "amount">("name");
  const [status, setStatus] = useState<"active" | "archived">("active");
  const ask = useConfirm();

  async function load() {
    const r = await api.listBills();
    setBills(r.bills);
  }
  useEffect(() => {
    load();
  }, []);

  if (!data) return <div className="state">Loading…</div>;

  const all = bills ?? [];
  const activeBills = all.filter((b) => b.active !== 0);
  const recurring = activeBills.filter((b) => !isOneOff(b));
  const recurringCount = recurring.length;
  // Yearly fees are shown as their monthly-equivalent (÷12) in the /month total.
  const monthlyTotal = recurring.reduce(
    (t, b) => t + (b.frequency === "annual" ? Math.round(b.amountMinor / 12) : b.amountMinor),
    0,
  );

  const shown = all
    .filter((b) => (status === "active" ? b.active !== 0 : b.active === 0))
    .filter((b) => (person === "both" ? true : b.paidByName === person))
    .filter((b) => (typeFilter === "all" ? true : b.type === typeFilter))
    .filter((b) => b.name.toLowerCase().includes(search.trim().toLowerCase()))
    .sort((a, b) => (sort === "amount" ? b.amountMinor - a.amountMinor : a.name.localeCompare(b.name)));

  const typeOf = (key: string) => data.types.find((t) => t.key === key) ?? { label: key, colour: "#98A2B3" };

  async function afterSave() {
    setModal(false);
    await load();
    await reload();
  }
  async function archive(b: Bill) {
    if (!(await ask({ title: "Archive bill?", message: `Archive "${b.name}"? It stays in your history but stops counting going forward.`, confirmLabel: "Archive" }))) return;
    await api.deleteBill(b.id);
    await load();
    await reload();
  }
  async function restore(b: Bill) {
    await api.restoreBill(b.id);
    await load();
    await reload();
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1 className="page__title">Recurring payments</h1>
          <p className="page__lede">Set up and manage every payment your tracker follows. {recurringCount} recurring · {formatMoney(monthlyTotal)}/month.</p>
        </div>
        <button className="btn btn--primary" onClick={() => { setEditBill(null); setModal(true); }}>+ Add payment</button>
      </div>

      <section className="bills-panel">
        <div className="bills-toolbar">
          <input className="input bills-search" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <Segmented value={status} onChange={(v) => setStatus(v as "active" | "archived")} options={[["active", "Active"], ["archived", "Archived"]]} />
          <Segmented value={person} onChange={setPerson} options={[["both", "Both"], ...data.household.members.map((m) => [m.name, m.name] as [string, string])]} />
          <div className="rec-filter">
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              ariaLabel="Filter by type"
              options={[{ value: "all", label: "All types" }, ...data.types.filter((t) => t.key !== "plan").map((t) => ({ value: t.key, label: t.label }))]}
            />
          </div>
          <div className="rec-filter">
            <Select
              value={sort}
              onChange={(v) => setSort(v as "name" | "amount")}
              ariaLabel="Sort"
              options={[{ value: "name", label: "Sort: Name" }, { value: "amount", label: "Sort: Amount" }]}
            />
          </div>
        </div>

        {bills == null ? (
          <div className="empty">Loading…</div>
        ) : shown.length === 0 ? (
          <div className="empty">{status === "archived" ? "Nothing archived." : "No payments match — add one to get started."}</div>
        ) : (
          <div className="rec-table">
            <div className="rec-table__head">
              <span>Name</span>
              <span>Type</span>
              <span>Category</span>
              <span className="ta-c">By</span>
              <span>Details</span>
              <span className="ta-r">Amount</span>
              <span />
            </div>
            {shown.map((b) => {
              const t = typeOf(b.type);
              const yearly = b.frequency === "annual";
              const oneOff = isOneOff(b);
              const cadence = yearly ? "Yearly" : oneOff ? "One-off" : "Monthly";
              const cadenceClass = yearly ? "cadence--year" : oneOff ? "cadence--once" : "cadence--rec";
              return (
                <div className={"rec-table__row" + (b.active === 0 ? " is-archived" : "")} key={b.id}>
                  <span className="rec-table__name">
                    <button className="rec-table__namebtn" onClick={() => { setEditBill(b); setModal(true); }} title="View / edit details">{b.name}</button>
                    <span className={"cadence " + cadenceClass}>{cadence}</span>
                  </span>
                  <span>
                    {b.type ? <span className="type-tag" style={{ background: t.colour + "22", color: t.colour }}>{t.label}</span> : "—"}
                  </span>
                  <span className="rec-table__cat">
                    {b.categoryName ? <><span className="chip__dot" style={{ background: b.categoryColour ?? "#98A2B3" }} />{b.categoryName}</> : "—"}
                  </span>
                  <span className="ta-c">
                    {b.paidByInitial ? <span className="avatar" style={{ background: b.paidByColour ?? "#98A2B3" }} title={b.paidByName ?? ""}>{b.paidByInitial}</span> : "—"}
                  </span>
                  <span className="rec-table__acc">{b.accountNumber || "—"}</span>
                  <span className="ta-r rec-table__amount">{formatMoney(b.amountMinor)}</span>
                  <span className="rec-table__icons">
                    <button className="icon-btn" onClick={() => { setEditBill(b); setModal(true); }} aria-label="Edit" title="Edit"><EditIcon /></button>
                    {b.active === 0 ? (
                      <button className="icon-btn" onClick={() => restore(b)} aria-label="Restore" title="Restore"><RestoreIcon /></button>
                    ) : (
                      <button className="icon-btn" onClick={() => archive(b)} aria-label="Archive" title="Archive"><ArchiveIcon /></button>
                    )}
                  </span>
                </div>
              );
            })}
            <div className="table-total table-total--flush">
              <span className="table-total__label">Total{status === "archived" ? " (archived)" : ""}</span>
              <span className="table-total__value">{formatMoney(shown.reduce((t, b) => t + b.amountMinor, 0))}</span>
            </div>
          </div>
        )}
      </section>

      {modal && <BillModal data={data} bill={editBill} onClose={() => setModal(false)} onSaved={afterSave} />}
    </div>
  );
}
